import { useSeo } from "@/lib/seo";
import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import AppHeader from '@/pages/home/components/AppHeader';
import Footer from '@/pages/home/components/Footer';
import AppEnding from '@/pages/home/components/AppEnding';
import Reveal from '@/pages/lot-size/components/Reveal';
import RiskLab, { type RiskTabId } from './components/RiskLab';
import FormulaSection from './components/FormulaSection';
import QuickAnswers from './components/QuickAnswers';
import BrokerSection from './components/BrokerSection';

const TAB_IDS: RiskTabId[] = ['risk', 'drawdown', 'ruin'];

function isTabId(value: string | null): value is RiskTabId {
  return value !== null && (TAB_IDS as string[]).includes(value);
}

export default function RiskPage() {
  useSeo({
    /* @replace:risk-title */
    title: 'Quản trị Rủi ro Giao dịch Forex | Công cụ & Chiến lược Bảo vệ Vốn - ForexMatrix',
    /* @replace:risk-description */
    description: 'Nâng cao hiệu quả Quản trị Rủi ro giao dịch Forex với bộ công cụ tính toán và chiến lược chuyên sâu từ ForexMatrix. Hướng dẫn quản lý vốn, kiểm soát drawdown và bảo vệ tài khoản tối ưu trước biến động thị trường.',
    /* @replace:risk-keywords */
    keywords: 'quản trị rủi ro forex, quản lý vốn giao dịch, chiến lược risk management, bảo vệ tài khoản forex, drawdown forex, forex matrix',
    path: '/quan-tri-rui-ro',
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: RiskTabId = isTabId(tabParam) ? tabParam : 'risk';

  const skipScrollRef = useRef(false);

  const setActiveTab = useCallback(
    (tab: RiskTabId) => {
      skipScrollRef.current = true;
      setSearchParams({ tab }, { replace: true });
    },
    [setSearchParams],
  );

  useEffect(() => {
    if (!tabParam) return;
    if (skipScrollRef.current) {
      skipScrollRef.current = false;
      return;
    }
    const el = document.getElementById('risk-lab');
    if (!el) return;
    const raf = requestAnimationFrame(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    return () => cancelAnimationFrame(raf);
  }, [tabParam]);

  return (
    <div className="relative min-h-screen bg-[#F8FAFC] text-slate-900">
      <AppHeader />
      <main className="pb-8">
        <div className="mx-auto max-w-6xl px-4 pt-28 pb-16 md:px-8 md:pt-32 md:pb-20">
          {/* Header */}
          <Reveal>
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700 ring-1 ring-violet-100">
                <i className="ri-shield-check-line" />
                Công cụ Quản trị Rủi ro
              </span>
              <h1 className="mt-4 font-display text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">
                Quản trị Rủi ro
              </h1>
              <p className="mt-4 text-base leading-relaxed text-slate-600 md:text-lg">
                Tính khối lượng lệnh theo % rủi ro, mô phỏng chuỗi thua để hình dung Drawdown và ước tính
                nguy cơ cháy tài khoản — tất cả trong một giao diện duy nhất.
              </p>
            </div>

            <ul className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3 sm:gap-6">
              <li className="rounded-xl border border-slate-200/80 bg-white p-5">
                <div className="h-1 w-10 rounded-full bg-emerald-500" />
                <p className="mt-3 text-sm font-semibold text-slate-900">Khối lượng theo rủi ro</p>
                <p className="mt-1.5 text-xs leading-relaxed text-slate-500">
                  Nhập tỷ lệ rủi ro và khoảng cách stop-loss — khối lượng lệnh được tính tự động để mỗi giao dịch chỉ mạo hiểm đúng phần trăm vốn cho phép.
                </p>
              </li>
              <li className="rounded-xl border border-slate-200/80 bg-white p-5">
                <div className="h-1 w-10 rounded-full bg-rose-500" />
                <p className="mt-3 text-sm font-semibold text-slate-900">Mô phỏng Drawdown</p>
                <p className="mt-1.5 text-xs leading-relaxed text-slate-500">
                  Thử nghiệm nhiều chuỗi thua liên tiếp để thấy trước mức sụt giảm tài khoản và biết cần bao nhiêu lệnh thắng để hồi phục vốn.
                </p>
              </li>
              <li className="rounded-xl border border-slate-200/80 bg-white p-5">
                <div className="h-1 w-10 rounded-full bg-amber-500" />
                <p className="mt-3 text-sm font-semibold text-slate-900">Nguy cơ cháy tài khoản</p>
                <p className="mt-1.5 text-xs leading-relaxed text-slate-500">
                  Ước tính xác suất cạn vốn dựa trên tỷ lệ thắng và mức rủi ro, giúp bạn đánh giá rủi ro trước khi quyết định vào lệnh.
                </p>
              </li>
            </ul>
          </Reveal>

          {/* Risk Management Lab */}
          <div id="risk-lab" className="mt-10 scroll-mt-24">
            <Reveal delay={80}>
              <RiskLab activeTab={activeTab} onChange={setActiveTab} />
            </Reveal>
          </div>

          {/* Content modules */}
          <div className="mt-16 space-y-12">
            <Reveal>
              <FormulaSection />
            </Reveal>
            <Reveal>
              <QuickAnswers />
            </Reveal>
            <Reveal>
              <BrokerSection />
            </Reveal>
          </div>
        </div>
      </main>
      <Footer />
      <AppEnding />
    </div>
  );
}